import { useState } from "react";
import { motion } from "framer-motion";
import { Envelope, Phone, MapPin, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = [
  "Mariage",
  "Anniversaire",
  "Baptême",
  "Soirée privée",
  "Événement corporate",
  "Autre"
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message envoyé avec succès !");
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };
  
  if (submitted) {
    return (
      <div className="pt-24 md:pt-32 lg:pt-36 min-h-screen flex items-center justify-center" data-testid="contact-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto px-4"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#C9A962] to-[#A88B4A] flex items-center justify-center">
            <CheckCircle size={40} weight="fill" className="text-black" />
          </div>
          <h2 className="text-3xl font-extrabold text-white mb-4">
            Message envoyé !
          </h2>
          <p className="text-[#B8A88A] mb-8">
            Merci pour votre message. Nous vous répondrons dans les plus brefs délais.
          </p>
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData({ name: "", email: "", phone: "", event_type: "", message: "" });
            }}
            className="btn-secondary"
            data-testid="send-another-btn"
          >
            Envoyer un autre message
          </button>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="pt-24 md:pt-32 lg:pt-36" data-testid="contact-page">
      <Seo
        title="Contact & Devis Photobooth Bourgoin-Jallieu | Pixora"
        description="Contactez Pixora pour la location d'un photobooth à Bourgoin-Jallieu ou en Nord-Isère. Devis gratuit, réponse rapide par email ou téléphone."
        path="/contact"
      />
      <section className="py-16 md:py-24 bokeh-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <Envelope size={20} className="text-[#C9A962]" weight="fill" />
              <span className="text-sm uppercase tracking-[0.2em] text-[#B8A88A]">
                Contact
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white mb-6">
              Contactez-<span className="gradient-text">nous</span>
            </h1>
            <p className="text-lg text-[#B8A88A] max-w-2xl mx-auto">
              Une question ? Un devis ? N'hésitez pas à nous contacter, nous vous répondrons rapidement.
            </p>
          </motion.div>
          
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="card bg-[#0A0A0A] rounded-2xl p-8"
            >
              <h3 className="text-2xl font-bold text-white mb-6">
                Envoyez-nous un message
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-medium text-[#B8A88A] mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="input-field"
                    placeholder="Votre nom"
                    data-testid="contact-name-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#B8A88A] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="input-field"
                    placeholder="votre@email.com"
                    data-testid="contact-email-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#B8A88A] mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="06 00 00 00 00"
                    data-testid="contact-phone-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#B8A88A] mb-2">
                    Type d'événement *
                  </label>
                  <select
                    name="event_type"
                    value={formData.event_type}
                    onChange={handleChange}
                    required
                    className="input-field appearance-none"
                    data-testid="contact-event-select"
                  >
                    <option value="">Sélectionnez...</option>
                    {eventTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#B8A88A] mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="input-field resize-none"
                    placeholder="Décrivez votre projet..."
                    data-testid="contact-message-input"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    "Envoi en cours..."
                  ) : (
                    <>
                      <PaperPlaneTilt size={20} weight="bold" />
                      Envoyer le message
                    </>
                  )}
                </button>
              </form>
            </motion.div>
            
            {/* Contact info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-8"
            >
              <div className="glass rounded-2xl p-8 border border-[#C9A962]/30">
                <h3 className="text-2xl font-bold text-white mb-6">
                  Informations
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#C9A962]/20 flex items-center justify-center flex-shrink-0">
                      <Envelope size={24} className="text-[#C9A962]" />
                    </div>
                    <div>
                      <p className="text-[#8A7A5A] text-sm mb-1">Email</p>
                      <a href="mailto:pixora.photobooth38@gmail.com" className="text-white hover:text-[#C9A962] transition-colors">
                        pixora.photobooth38@gmail.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#C9A962]/20 flex items-center justify-center flex-shrink-0">
                      <Phone size={24} className="text-[#C9A962]" />
                    </div>
                    <div>
                      <p className="text-[#8A7A5A] text-sm mb-1">Téléphone</p>
                      <a href="tel:0745709881" className="text-white hover:text-[#C9A962] transition-colors">
                        07 45 70 98 81
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Zone */}
              <div className="glass rounded-2xl p-8 border border-[#C9A962]/30">
                <h3 className="text-xl font-bold text-white mb-6">
                  Zone d'intervention
                </h3>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#C9A962]/20 flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} weight="fill" className="text-[#C9A962]" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Nord Isère & Est Lyonnais</p>
                    <p className="text-[#B8A88A] text-sm mt-1">225 avenue des marronniers</p>
                    <p className="text-[#B8A88A] text-sm">38300 Bourgoin-Jallieu</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
